import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { LoadingSkeleton } from '@/components/base/LoadingSkeleton';
import { ErrorState } from '@/components/base/ErrorState';
import ArticleCard from './components/ArticleCard';
import ArticleDetailModal from './components/ArticleDetailModal';
import KnowledgeBaseFilters from './components/KnowledgeBaseFilters';
import type { KbArticle, KbCategory } from '@/mocks/adminKnowledgeBase';
import { kbArticles, categoryMeta, allCategories } from '@/mocks/adminKnowledgeBase';

const formatter = new Intl.NumberFormat('es');

export default function AdminKnowledgeBase() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<KbCategory | 'todas'>('todas');
  const [selectedArticle, setSelectedArticle] = useState<KbArticle | null>(null);
  const [pageState, setPageState] = useState<'loading' | 'ready' | 'error'>('loading');

  const loadData = useCallback(() => {
    setPageState('loading');
    setTimeout(() => {
      const shouldError = Math.random() < 0.06;
      setPageState(shouldError ? 'error' : 'ready');
    }, 500);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const totals = useMemo(() => {
    const result: Record<KbCategory | 'todas', number> = { todas: kbArticles.length };
    allCategories.forEach((cat) => {
      result[cat] = kbArticles.filter((a) => a.category === cat).length;
    });
    return result;
  }, []);

  const stats = useMemo(() => {
    const totalViews = kbArticles.reduce((sum, a) => sum + a.views, 0);
    const featuredCount = kbArticles.filter((a) => a.featured).length;
    const avgReadTime = Math.round(
      kbArticles.reduce((sum, a) => sum + parseInt(a.readTime, 10), 0) / kbArticles.length,
    );
    return { total: kbArticles.length, featured: featuredCount, totalViews, avgReadTime };
  }, []);

  const featuredArticles = useMemo(() => {
    return kbArticles.filter((a) => a.featured);
  }, []);

  const filteredArticles = useMemo(() => {
    let result = kbArticles;
    if (activeCategory !== 'todas') {
      result = result.filter((a) => a.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q) ||
          categoryMeta[a.category].label.toLowerCase().includes(q),
      );
    }
    return result;
  }, [activeCategory, searchQuery]);

  const handleOpenArticle = useCallback(
    (article: KbArticle) => {
      setSelectedArticle(article);
    },
    [],
  );

  const handleNavigate = useCallback(
    (article: KbArticle) => {
      setSelectedArticle(article);
    },
    [],
  );

  return (
    <div className="min-h-screen bg-background-100">
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-book-open-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Base de Conocimiento</h1>
              <p className="text-foreground-400 text-[9px]">{stats.total} artículos · {stats.featured} destacados</p>
            </div>
          </div>
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>Volver
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl bg-accent-100 flex items-center justify-center">
                <i className="ri-book-open-line text-accent-600 text-base"></i>
              </div>
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Base de Conocimiento</h2>
                <p className="text-foreground-400 text-xs">{stats.total} artículos · {stats.featured} destacados · {formatter.format(stats.totalViews)} vistas totales</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/support"
                className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-400 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-customer-service-2-line text-xs mr-1"></i>Soporte
              </Link>
              <Link
                to="/admin/dashboard"
                className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-400 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-dashboard-line text-xs mr-1"></i>Dashboard
              </Link>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-28 lg:pb-8">
            <div className="max-w-[960px] mx-auto space-y-5">
              {pageState === 'loading' && <LoadingSkeleton variant="table" rows={5} cols={4} />}

              {pageState === 'error' && (
                <ErrorState
                  title="Error al cargar la base de conocimiento"
                  message="No pudimos obtener los artículos. Intentá de nuevo."
                  onRetry={loadData}
                />
              )}

              {pageState === 'ready' && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      {
                        label: 'Total Artículos',
                        count: stats.total,
                        icon: 'ri-article-line',
                        iconBg: 'bg-background-200',
                        iconColor: 'text-foreground-500',
                        color: 'bg-background-50 border-background-200',
                      },
                      {
                        label: 'Destacados',
                        count: stats.featured,
                        icon: 'ri-star-fill',
                        iconBg: 'bg-amber-100',
                        iconColor: 'text-amber-600',
                        color: 'bg-amber-50 border-amber-200',
                      },
                      {
                        label: 'Vistas Totales',
                        count: formatter.format(stats.totalViews),
                        icon: 'ri-eye-line',
                        iconBg: 'bg-sky-100',
                        iconColor: 'text-sky-600',
                        color: 'bg-sky-50 border-sky-200',
                      },
                      {
                        label: 'Lectura Promedio',
                        count: stats.avgReadTime + ' min',
                        icon: 'ri-time-line',
                        iconBg: 'bg-emerald-100',
                        iconColor: 'text-emerald-600',
                        color: 'bg-emerald-50 border-emerald-200',
                      },
                    ].map((s) => (
                      <div
                        key={s.label}
                        className={`rounded-2xl border p-4 text-left ${s.color}`}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${s.iconBg}`}>
                            <i className={`${s.icon} ${s.iconColor} text-sm`}></i>
                          </div>
                        </div>
                        <p className="text-2xl font-bold text-foreground-950">{s.count}</p>
                        <p className="text-foreground-400 text-[10px] mt-0.5">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  {activeCategory === 'todas' && !searchQuery && featuredArticles.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-5 h-5 rounded-md bg-amber-100 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-600 text-[10px]"></i>
                        </div>
                        <h3 className="text-foreground-800 font-bold text-sm">Artículos Destacados</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {featuredArticles.map((article) => (
                          <ArticleCard
                            key={article.id}
                            article={article}
                            onClick={handleOpenArticle}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <KnowledgeBaseFilters
                    onSearch={setSearchQuery}
                    onCategoryChange={setActiveCategory}
                    activeCategory={activeCategory}
                    totals={totals}
                  />

                  {filteredArticles.length === 0 ? (
                    <div className="text-center py-16 bg-background-50 rounded-2xl border border-background-200">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-4">
                        <i className="ri-book-open-line text-foreground-400 text-xl"></i>
                      </div>
                      <h3 className="text-foreground-800 font-bold text-sm mb-1">Sin resultados</h3>
                      <p className="text-foreground-400 text-xs max-w-xs mx-auto leading-relaxed">
                        {searchQuery
                          ? `No encontramos artículos que coincidan con "${searchQuery}". Probá con otros términos.`
                          : `No hay artículos en la categoría "${categoryMeta[activeCategory as KbCategory]?.label || 'seleccionada'}".`}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(activeCategory !== 'todas' || searchQuery) && (
                        <div className="flex items-center gap-2">
                          <h3 className="text-foreground-800 font-bold text-sm">
                            {filteredArticles.length} artículo{filteredArticles.length !== 1 ? 's' : ''}
                          </h3>
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {filteredArticles.map((article) => (
                          <ArticleCard
                            key={article.id}
                            article={article}
                            onClick={handleOpenArticle}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Backend Pending Indicator */}
                  <div className="bg-amber-50/80 rounded-2xl border border-amber-200 p-3 flex items-center gap-2.5">
                    <div className="w-6 h-6 rounded-lg bg-amber-200 flex items-center justify-center shrink-0">
                      <i className="ri-tools-line text-amber-700 text-xs"></i>
                    </div>
                    <p className="text-[10px] text-amber-700 leading-relaxed">
                      <strong>Frontend mock.</strong> Los artículos y la retroalimentación (útil/no útil) se almacenan solo durante la sesión. La persistencia, analytics de lectura y búsqueda avanzada requerirán integración con backend (Cursor).
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      {selectedArticle && (
        <ArticleDetailModal
          article={selectedArticle}
          onClose={() => setSelectedArticle(null)}
          onNavigate={handleNavigate}
        />
      )}

      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-background-50/98 backdrop-blur-2xl border-t border-background-200">
        <div className="flex items-center justify-around h-[56px] px-1">
          {[
            { icon: 'ri-dashboard-line', label: 'Dashboard', active: false },
            { icon: 'ri-file-list-3-line', label: 'Contenido', active: false },
            { icon: 'ri-book-open-fill', label: 'Conocimiento', active: true },
            { icon: 'ri-broadcast-line', label: 'Cabina', active: false },
            { icon: 'ri-settings-3-line', label: 'Ajustes', active: false },
          ].map((item) => (
            <button
              key={item.label}
              className={`flex flex-col items-center justify-center gap-0.5 h-full min-w-0 flex-1 cursor-pointer transition-colors relative ${
                item.active ? 'text-accent-600' : 'text-foreground-400'
              }`}
            >
              <i className={`${item.icon} text-xl`}></i>
              <span className="text-[10px] font-semibold">{item.label}</span>
              {item.active && (
                <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-accent-500 rounded-full"></span>
              )}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}